// ============================================================================
// FIDÉLITÉ › RETOURS À CORRIGER — les retours qui n'ont rien défait de la fidélité de leur vente, puis le geste qui
// pose ce qui manque (outil serveur : `crm/return-loyalty-repair.ts`).
//
// ⚠️ D'ABORD LA LISTE, PUIS LE GESTE (décision utilisateur) : le bouton ne corrige que les lignes MONTRÉES, et le
//    serveur rejoue la règle au moment d'écrire — un retour corrigé entre-temps par sa caisse n'est jamais doublé.
// ⚠️ MUET QUAND IL N'Y A RIEN : un bandeau permanent cesse d'être lu. MAIS UNE LECTURE EN ÉCHEC SE DIT — c'est un outil de
//    correction d'argent, et son silence se lirait « rien à corriger ».
// ⚠️ UNE LIGNE « EN ATTENTE » N'A PAS DE BOUTON : sa caisse n'a encore rien envoyé de plus récent, son écriture peut être
//    en route. Le dire vaut mieux qu'un bouton qui répondrait « pas encore » (§23.0, bouton qui fait semblant).
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { showToast, useLatest } from '@orgazone/ui-web';
import { formatMoney } from '@orgazone/money-format';
import { api, type ReturnLoyaltyRepair } from '../api';
import { horodatageFiscal } from '../periode';
import { t } from '../i18n';

const money = (c: number): string => (c > 0 ? formatMoney(c) : '—');

export function ReturnRepairsBanner({ gln }: { gln: string | null }) {
  const [rows, setRows] = useState<ReturnLoyaltyRepair[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA LISTE : celle de l'ancien magasin, revenue en dernier, ferait corriger dans la
  //    base du magasin affiché des retours qui n'y sont pas.
  const latest = useLatest();
  const reload = useCallback(() => {
    if (!gln) return;
    const isLatest = latest();
    api.returnLoyaltyRepairs()
      .then((r) => { if (isLatest()) { setRows(r.repairs); setErr(null); } })
      .catch((e) => { if (isLatest()) { setRows(null); setErr(e instanceof Error ? e.message : String(e)); } });
  }, [gln, latest]);
  useEffect(reload, [reload]);

  if (err) {
    return (
      <div role="alert" style={{ padding: '11px 14px', marginBottom: 14, borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-dense)' }}>
        {t('Les retours à corriger n’ont pas pu être lus : {v}', { v: err })}
      </div>
    );
  }
  if (!rows || rows.length === 0) return null;

  const ready = rows.filter((r) => r.ready);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.repairReturnLoyalty(ready.map((x) => x.returnId));
      // ⚠️ LE MESSAGE DIT CE QUI S'EST VRAIMENT PASSÉ, ce qui n'a pas été écrit compris : « corrigé » sur un retour resté
      //    en attente serait un succès qui n'a pas eu lieu.
      const parts = [t('{n} retour corrigé', { n: r.repaired.length })];
      if (r.waiting.length > 0) parts.push(t('{n} en attente de sa caisse', { n: r.waiting.length }));
      if (r.nothingToDo.length > 0) parts.push(t('{n} n’avait plus rien à corriger', { n: r.nothingToDo.length }));
      showToast(parts.join(' · '), 'ok', 8000);
    } catch (e) {
      showToast(t('Correction refusée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally {
      setBusy(false);
      reload();
    }
  };

  return (
    <div style={{ marginBottom: 18, border: '1px solid var(--amber-fg)', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 240, fontSize: 'var(--t-dense)', color: 'var(--amber-fg)' }}>
          <strong>{t('{n} retour n’a pas défait la fidélité de sa vente.', { n: rows.length })}</strong>{' '}
          {t('Le gain n’a pas été repris, ou la remise fidélité pas rendue : le programme était éteint, la vente venait d’une autre caisse, ou sa lecture a échoué.')}
        </span>
        {/* ⚠️ PAS DE BOUTON QUAND RIEN N'EST PRÊT : il ne pourrait rien écrire. */}
        {ready.length > 0 && (
          <button
            type="button" onClick={() => { void run(); }} disabled={busy}
            style={{
              height: 30, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)',
              background: 'var(--amber-fg)', color: 'var(--paper)', fontSize: 'var(--t-dense)', fontWeight: 700,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
            }}
          >
            {busy ? t('Correction…') : t('Corriger {n} retour', { n: ready.length })}
          </button>
        )}
      </div>
      <div style={{ overflowX: 'auto', background: 'var(--paper)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-dense)' }}>
          <thead>
            <tr style={{ color: 'var(--ink-500)', textAlign: 'left' }}>
              <th style={th}>{t('Pièce de retour')}</th>
              <th style={th}>{t('Vente d’origine')}</th>
              <th style={th}>{t('Client')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('Gain à reprendre')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('Remise à rendre')}</th>
              <th style={th}>{t('État')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.returnId} style={{ borderTop: '1px solid var(--ink-100)' }}>
                <td style={td}>{r.returnNumber} <span style={{ color: 'var(--ink-500)' }}>{horodatageFiscal(r.returnAt)}</span></td>
                <td style={td}>{r.originNumber}</td>
                <td style={td}>{r.clientName}</td>
                <td className="tnum" style={{ ...td, textAlign: 'right' }}>{money(r.clawbackCents)}</td>
                <td className="tnum" style={{ ...td, textAlign: 'right' }}>{money(r.refundCents)}</td>
                <td style={{ ...td, color: r.ready ? 'var(--ink-700)' : 'var(--ink-500)' }}>
                  {r.ready ? t('Prêt à corriger') : t('En attente : la caisse n’a encore rien envoyé de plus récent')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: '7px 14px', fontWeight: 600, whiteSpace: 'nowrap' } as const;
const td = { padding: '7px 14px', color: 'var(--ink-900)' } as const;
